import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  Check,
  X,
  ArrowRight,
  Sparkles,
  Camera,
  Building2,
  Crown,
  Info,
  Globe,
  Lock,
  Layers,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { getLangFromPath, localizePath } from '../i18n';

const plans = [
  {
    id: 'photographer',
    audience: 'fotografos',
    tag: 'Para fotógrafos y estudios',
    Icon: Camera,
    name: 'Fotógrafos',
    price: 'Gratis',
    priceSuffix: 'para siempre',
    subtitle: 'Sin cargo mensual, sin límites. Todo lo que necesitás para vivir de vender tus fotos.',
    bullets: [
      'Paneles y analytics de tus fotos',
      'Market intel: eventos, tendencias, precios promedio',
      'Próximos eventos calendarizados',
      'Acreditaciones a eventos exclusivos de momentIA',
      'Álbumes privados por rostro (reconocimiento facial IA)',
      'Múltiples formas de compartir con el usuario',
      'Visibilidad total: tus fotos aparecen cuando el usuario te busca en cualquier evento',
      'Pagos integrados (Mercado Pago + Stripe)',
      'Retención de fotos: 4 meses',
    ],
    commission: 'momentIA cobra 30% del precio de cada foto vendida.',
    ctaLabel: 'Empezar gratis',
    ctaTo: '/vende-tus-fotos',
    accent: {
      gradFrom: 'from-emerald-500',
      gradTo: 'to-teal-500',
      text: 'text-emerald-300',
      badge: 'bg-emerald-500/12 border-emerald-400/25 text-emerald-200',
      btn: 'bg-white text-[#0a0a0f] hover:bg-white/90',
    },
    highlighted: false,
  },
  {
    id: 'business',
    audience: 'empresas',
    tag: 'Para empresas',
    Icon: Building2,
    name: 'Empresas',
    price: 'A consultar',
    priceSuffix: 'Contactanos para tu propuesta',
    annualPrice: 'A consultar',
    annualSuffix: 'Contactanos para tu propuesta',
    annualMonthlyEq: '',
    annualSavings: '',
    subtitle: 'Landing branded y ecosistema abierto. Ideal para arrancar con momentIA.',
    bullets: [
      'Eventos y fotos ilimitadas',
      'Landing branded (colores + logo) · "Powered by momentIA"',
      'Ecosistema abierto: el usuario encuentra sus fotos de otros eventos en momentIA',
      'Ejecutivo de cuenta dedicado',
      'Paneles en tiempo real (asistentes, shares)',
      'Distribución multicanal (email, WhatsApp, QR)',
      'Data y trazabilidad · exports CSV / Excel',
      'Onboarding y capacitación del equipo',
      'Soporte prioritario del equipo momentIA (12×5)',
      'Retención de fotos: 8 meses',
    ],
    commission: 'Sin comisión por foto · Landing "Powered by momentIA"',
    ctaLabel: 'Contratar Empresas',
    ctaTo: '/request-demo',
    accent: {
      gradFrom: 'from-violet-500',
      gradTo: 'to-indigo-500',
      text: 'text-violet-300',
      badge: 'bg-violet-500/12 border-violet-400/30 text-violet-200',
      btn: 'bg-white text-[#0a0a0f] hover:bg-white/90',
    },
    highlighted: false,
  },
  {
    id: 'pro',
    audience: 'empresas',
    tag: 'Recomendado · Para marcas que activan',
    Icon: Sparkles,
    name: 'Pro',
    price: 'A consultar',
    priceSuffix: 'Contactanos para tu propuesta',
    annualPrice: 'A consultar',
    annualSuffix: 'Contactanos para tu propuesta',
    annualMonthlyEq: '',
    annualSavings: '',
    subtitle: 'Ecosistema cerrado, sponsor slots y emails desde tu propio dominio. Para marcas que buscan monetizar cada evento.',
    bullets: [
      'Eventos y fotos ilimitadas',
      'Landing branded (colores + logo)',
      'Ecosistema cerrado: los usuarios solo ven las fotos de tu marca',
      'Sponsor slots + cupones con tracking de clicks y canjes',
      'Emails con tu dominio de marca (SPF / DKIM configurado por vos)',
      'Ejecutivo de cuenta dedicado',
      'Paneles en tiempo real (asistentes, shares, sponsors)',
      'Distribución multicanal (email, WhatsApp, QR)',
      'Data y trazabilidad · exports CSV / Excel',
      'Onboarding y capacitación del equipo',
      'Soporte prioritario del equipo momentIA (12×5)',
      'Retención de fotos: 8 meses',
    ],
    commission: 'Sin comisión por foto · Ecosistema cerrado + sponsors',
    ctaLabel: 'Contratar Pro',
    ctaTo: '/request-demo',
    accent: {
      gradFrom: 'from-fuchsia-500',
      gradTo: 'to-pink-500',
      text: 'text-fuchsia-300',
      badge: 'bg-fuchsia-500/12 border-fuchsia-400/30 text-fuchsia-200',
      btn: 'bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:brightness-110 text-white',
    },
    highlighted: true,
  },
  {
    id: 'enterprise',
    audience: 'empresas',
    tag: 'Para grupos y marcas globales',
    Icon: Crown,
    name: 'Enterprise',
    price: 'Custom',
    priceSuffix: 'a medida',
    subtitle: 'Tu tecnología, tu dominio, tu ecosistema. Todo configurable: cerrado o abierto, un evento o cientos.',
    bullets: [
      'Dominio propio (tu.marca.com) · como tecnología propia',
      'Ecosistema configurable: cerrado o abierto',
      'Multi-marca / multi-propiedad',
      'Volumen ilimitado (eventos + fotos)',
      'Sin la marca "Powered by momentIA"',
      'SSO (SAML / OIDC) opcional',
      'Data lake export nativo (S3 / Snowflake)',
      'SLA contractual + arquitectura dedicada',
      'Regulación local (GDPR, LGPD, HIPAA)',
      'Customer Success + Solutions Architect',
      'Onsite training + eventos co-branded',
      'Roadmap compartido con features a medida',
      'Equipo de soporte dedicado y brandeado con tu marca (24×7)',
      'Retención de fotos: 8 meses o configurable',
    ],
    commission: 'Contrato anual o plurianual · pricing por volumen',
    ctaLabel: 'Hablar con ventas',
    ctaTo: '/request-demo',
    accent: {
      gradFrom: 'from-amber-400',
      gradTo: 'to-orange-500',
      text: 'text-amber-300',
      badge: 'bg-amber-500/12 border-amber-400/30 text-amber-200',
      btn: 'bg-white text-[#0a0a0f] hover:bg-white/90',
    },
    highlighted: false,
  },
];

function Cell({ value, accent }) {
  if (value === true) {
    return (
      <div className="flex justify-center">
        <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${accent.gradFrom} ${accent.gradTo} flex items-center justify-center`}>
          <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
        </div>
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="flex justify-center">
        <div className="w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
          <X className="w-3.5 h-3.5 text-white/30" />
        </div>
      </div>
    );
  }
  return <div className="text-center text-sm text-white/85 font-medium px-2 leading-snug">{value}</div>;
}

export default function Pricing() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const to = (p) => localizePath(p, lang);
  const [audience, setAudience] = useState('empresas');
  const [billing, setBilling] = useState('monthly');
  const visiblePlans = plans.filter((p) => p.audience === audience);
  const isEmpresas = audience === 'empresas';
  const isAnnual = billing === 'annual';

  return (
    <>
      <SEO
        title={t('pricing.seoTitle')}
        description={t('pricing.seoDescription')}
        path="/precios"
      />
      {/* HERO */}
      <section className="relative pt-16 lg:pt-24 pb-10 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-violet-600/15 rounded-full blur-[140px]" />
          <div className="absolute top-40 -right-40 w-[400px] h-[400px] bg-amber-500/10 rounded-full blur-[140px]" />
          <div className="absolute top-40 -left-40 w-[400px] h-[400px] bg-emerald-500/10 rounded-full blur-[140px]" />
        </div>
        <div className="container-mi relative text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-4">{t('pricing.eyebrow')}</div>
          <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl mx-auto">
            {t('pricing.titleLine1')}
            <span className="block bg-gradient-to-r from-emerald-300 via-fuchsia-300 to-amber-300 bg-clip-text text-transparent">
              {t('pricing.titleHighlight')}
            </span>
          </h1>
          <p className="mt-5 text-lg text-white/70 max-w-2xl mx-auto">
            {t('pricing.subtitle')} <span className="text-emerald-300 font-medium">{t('pricing.subtitleAccent')}</span>
          </p>

          {/* Segmented control */}
          <div className="mt-10 inline-flex items-center gap-1 p-1.5 rounded-full bg-[#111116] border border-white/10">
            <button
              data-testid="pricing-tab-empresas"
              onClick={() => setAudience('empresas')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                isEmpresas
                  ? 'bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white shadow-lg shadow-fuchsia-500/30'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              <Building2 className="inline w-4 h-4 mr-1.5 -mt-0.5" />
              {t('pricing.tabCompany')}
            </button>
            <button
              data-testid="pricing-tab-fotografos"
              onClick={() => setAudience('fotografos')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                !isEmpresas
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/30'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              <Camera className="inline w-4 h-4 mr-1.5 -mt-0.5" />
              {t('pricing.tabPhotographer')}
            </button>
          </div>

          {/* Billing toggle hidden while empresa pricing is on request. */}
          {false && isEmpresas && (
            <div className="mt-5 flex justify-center">
              <div className="inline-flex items-center gap-1 p-1 rounded-full bg-[#0e0e13] border border-white/10">
                <button
                  data-testid="pricing-billing-monthly"
                  onClick={() => setBilling('monthly')}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    !isAnnual
                      ? 'bg-white text-[#0a0a0f]'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  Mensual
                </button>
                <button
                  data-testid="pricing-billing-annual"
                  onClick={() => setBilling('annual')}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all inline-flex items-center gap-1.5 ${
                    isAnnual
                      ? 'bg-white text-[#0a0a0f]'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  Anual
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isAnnual ? 'bg-emerald-500/15 text-emerald-600' : 'bg-emerald-500/20 text-emerald-300'} font-bold`}>
                    −20%
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* PLANS */}
      <section className="pb-8">
        <div className="container-mi">
          <div
            className={`grid gap-5 ${
              isEmpresas
                ? 'md:grid-cols-3 max-w-6xl mx-auto'
                : 'max-w-2xl mx-auto'
            }`}
          >
            {visiblePlans.map((plan) => {
              const Icon = plan.Icon;
              return (
                <div
                  key={plan.id}
                  data-testid={`pricing-plan-${plan.id}`}
                  className={`relative rounded-3xl p-7 lg:p-8 flex flex-col ${
                    plan.highlighted
                      ? 'bg-gradient-to-b from-[#251a2a] to-[#111116] border border-fuchsia-400/30 shadow-2xl shadow-fuchsia-900/30 lg:-my-4 lg:py-12'
                      : 'bg-[#111116] border border-white/8'
                  }`}
                >
                  {plan.highlighted && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white text-xs font-semibold shadow-lg shadow-fuchsia-500/40">
                      <Sparkles className="w-3 h-3" /> {t('pricing.recommended')}
                    </div>
                  )}

                  <div className={`inline-flex items-center gap-2 self-start px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold border ${plan.accent.badge}`}>
                    {t(`pricing.plans.${plan.id}.tag`)}
                  </div>

                  <div className="mt-4 flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${plan.accent.gradFrom} ${plan.accent.gradTo} flex items-center justify-center text-white shadow-lg`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-display text-2xl font-bold text-white leading-none">{t(`pricing.plans.${plan.id}.name`)}</div>
                    </div>
                  </div>

                  <div className="mt-5 flex items-end gap-2">
                    <span className="font-display text-5xl font-bold text-white leading-none">
                      {isAnnual && plan.annualPrice ? plan.annualPrice : t(`pricing.plans.${plan.id}.price`)}
                    </span>
                    <span className="text-white/50 pb-1.5 text-sm">
                      {isAnnual && plan.annualSuffix ? plan.annualSuffix : t(`pricing.plans.${plan.id}.priceSuffix`)}
                    </span>
                  </div>
                  {isAnnual && plan.annualMonthlyEq && (
                    <div className="mt-2 inline-flex items-center gap-2 text-[11px]">
                      <span className="text-white/60">Equivale a </span>
                      <span className="text-white font-semibold">{plan.annualMonthlyEq}/mes</span>
                      <span className="text-emerald-300 font-semibold">· {plan.annualSavings}</span>
                    </div>
                  )}
                  <p className="mt-3 text-sm text-white/60 leading-relaxed min-h-[56px]">{t(`pricing.plans.${plan.id}.subtitle`)}</p>

                  <Link to={to(plan.ctaTo)} className="mt-6">
                    <Button
                      data-testid={`pricing-plan-cta-${plan.id}`}
                      className={`w-full h-11 rounded-full font-medium ${plan.accent.btn}`}
                    >
                      {t(`pricing.plans.${plan.id}.ctaLabel`)}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>

                  <div className={`mt-4 rounded-xl px-3.5 py-2.5 text-[12px] leading-snug ${plan.accent.badge} border`}>
                    <span className={`font-semibold ${plan.accent.text}`}>{t('pricing.commissionLabel')} </span>
                    <span className="text-white/80">{t(`pricing.plans.${plan.id}.commission`)}</span>
                  </div>

                  <div className="mt-5 pt-5 border-t border-white/10 flex-1">
                    <div className="text-[11px] uppercase tracking-wider text-white/40 mb-3 font-semibold">
                      {t('pricing.includesLabel')}
                    </div>
                    <ul className="space-y-2.5">
                      {(t(`pricing.plans.${plan.id}.bullets`, { returnObjects: true }) || []).map((f) => (
                        <li key={f} className="flex items-start gap-2.5">
                          <div className={`w-4 h-4 rounded-full bg-gradient-to-br ${plan.accent.gradFrom} ${plan.accent.gradTo} flex items-center justify-center shrink-0 mt-0.5`}>
                            <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />
                          </div>
                          <span className="text-[14px] text-white/85 leading-snug">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Callout: photographers not included — only on empresas tab */}
          {isEmpresas && (
            <div className="mt-8 max-w-4xl mx-auto rounded-2xl border border-white/10 bg-[#0e0e13] px-5 md:px-7 py-5 flex items-start md:items-center gap-4 flex-col md:flex-row">
            <div className="w-10 h-10 rounded-xl bg-violet-500/15 border border-violet-400/25 flex items-center justify-center text-violet-300 shrink-0">
              <Info className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-white">{t('pricing.callout.title')}</div>
              <div className="text-sm text-white/60 mt-1 leading-relaxed">
                {t('pricing.callout.desc')}
              </div>
            </div>
            <Link to="/request-demo">
              <Button data-testid="pricing-need-photographers-btn" className="bg-white text-[#0a0a0f] hover:bg-white/90 h-10 px-5 rounded-full text-sm font-semibold">
                {t('pricing.callout.cta')}
              </Button>
            </Link>
          </div>
          )}
        </div>
      </section>

      {/* ENTERPRISE HIGHLIGHT — only on empresas tab */}
      {isEmpresas && (
      <section className="py-20 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/12 via-orange-500/8 to-violet-500/12 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-amber-500/15 rounded-full blur-[160px]" />
        <div className="container-mi relative">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-3 flex items-center gap-2 justify-center">
              <Crown className="w-3.5 h-3.5" /> {t('pricing.enterpriseHighlight.eyebrow')}
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('pricing.enterpriseHighlight.titleLine1')}
              <span className="block bg-gradient-to-r from-amber-300 via-orange-300 to-violet-300 bg-clip-text text-transparent">
                {t('pricing.enterpriseHighlight.titleHighlight')}
              </span>
            </h2>
            <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
              {t('pricing.enterpriseHighlight.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {/* Empresas — abierto */}
            <div className="rounded-3xl bg-[#111116] border border-violet-400/20 p-7">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white shadow-lg">
                  <Globe className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-violet-300 font-semibold">{t('pricing.enterpriseHighlight.empresas.eyebrow')}</div>
                  <div className="font-display text-xl font-bold text-white">{t('pricing.enterpriseHighlight.empresas.title')}</div>
                </div>
              </div>
              <div className="rounded-xl bg-[#0a0a10] border border-white/8 p-4 mb-4">
                <div className="flex items-center justify-between text-[11px] text-white/60 mb-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  </div>
                  <div className="text-[10px]">momentia.shop/tu-evento</div>
                </div>
                <div className="text-xs text-white/70 mb-2 flex items-center gap-2">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 font-semibold">{t('pricing.enterpriseHighlight.empresas.poweredBy')}</span>
                </div>
                <div className="text-sm font-semibold text-white mb-1">{t('pricing.enterpriseHighlight.empresas.eventTitle')}</div>
                <div className="text-[11px] text-white/50 mb-3">{t('pricing.enterpriseHighlight.empresas.eventSubtitle')}</div>
                <div className="rounded-lg bg-white/5 border border-white/8 p-2 text-[10px] text-white/70 leading-relaxed">
                  {t('pricing.enterpriseHighlight.empresas.callout')}
                </div>
              </div>
              <ul className="space-y-2 text-sm text-white/75">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-violet-300 shrink-0 mt-0.5" />
                  {t('pricing.enterpriseHighlight.empresas.bullet1')}
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-violet-300 shrink-0 mt-0.5" />
                  <span>{t('pricing.enterpriseHighlight.empresas.bullet2Prefix')} <b>{t('pricing.enterpriseHighlight.empresas.bullet2Bold')}</b> {t('pricing.enterpriseHighlight.empresas.bullet2Suffix')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-violet-300 shrink-0 mt-0.5" />
                  {t('pricing.enterpriseHighlight.empresas.bullet3')}
                </li>
              </ul>
            </div>

            {/* Enterprise — configurable */}
            <div className="rounded-3xl bg-gradient-to-b from-[#2a1e10] to-[#111116] border border-amber-400/30 p-7 shadow-2xl shadow-amber-900/20">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-lg">
                  <Layers className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold">{t('pricing.enterpriseHighlight.enterprise.eyebrow')}</div>
                  <div className="font-display text-xl font-bold text-white">{t('pricing.enterpriseHighlight.enterprise.title')}</div>
                </div>
              </div>
              <div className="rounded-xl bg-[#0a0a10] border border-white/8 p-4 mb-4">
                <div className="flex items-center justify-between text-[11px] text-white/60 mb-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  </div>
                  <div className="text-[10px]">fotos.tu-marca.com</div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 rounded bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <div className="w-2.5 h-2.5 rounded-sm bg-white" />
                  </div>
                  <span className="text-xs font-bold text-white">tu-marca.com</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-semibold ml-auto flex items-center gap-1">
                    <Lock className="w-2.5 h-2.5" /> {t('pricing.enterpriseHighlight.enterprise.closedBadge')}
                  </span>
                </div>
                <div className="text-sm font-semibold text-white mb-1">{t('pricing.enterpriseHighlight.enterprise.eventTitle')}</div>
                <div className="text-[11px] text-white/50 mb-3">{t('pricing.enterpriseHighlight.enterprise.eventSubtitle')}</div>
                <div className="rounded-lg bg-amber-500/10 border border-amber-400/20 p-2 text-[10px] text-amber-100 leading-relaxed">
                  {t('pricing.enterpriseHighlight.enterprise.calloutPrefix')} <b>{t('pricing.enterpriseHighlight.enterprise.calloutBold')}</b>{t('pricing.enterpriseHighlight.enterprise.calloutSuffix')}
                </div>
              </div>
              <ul className="space-y-2 text-sm text-white/85">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                  <span><b>{t('pricing.enterpriseHighlight.enterprise.bullet1Bold')}</b> {t('pricing.enterpriseHighlight.enterprise.bullet1Suffix')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                  <span>{t('pricing.enterpriseHighlight.enterprise.bullet2Prefix')} <b>{t('pricing.enterpriseHighlight.enterprise.bullet2Bold1')}</b> {t('pricing.enterpriseHighlight.enterprise.bullet2Middle')} <b>{t('pricing.enterpriseHighlight.enterprise.bullet2Bold2')}</b></span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                  {t('pricing.enterpriseHighlight.enterprise.bullet3')}
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                  {t('pricing.enterpriseHighlight.enterprise.bullet4')}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* COMPARISON TABLE — only on empresas tab (fotografos has 1 plan) */}
      {isEmpresas && (
      <section className="py-20 lg:py-24">
        <div className="container-mi">
          <div className="max-w-3xl mb-10">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3">{t('pricing.comparison.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('pricing.comparison.titleLine1')} <span className="bg-gradient-to-r from-emerald-300 via-violet-300 to-amber-300 bg-clip-text text-transparent">{t('pricing.comparison.titleHighlight')}</span>
            </h2>
          </div>

          <div className="rounded-3xl border border-white/10 bg-[#0c0c12] overflow-hidden">
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] items-center border-b border-white/10 bg-[#0a0a10] sticky top-16 lg:top-20 z-10 backdrop-blur-md">
              <div className="px-4 md:px-6 py-4 text-[11px] uppercase tracking-wider text-white/40 font-semibold">
                {t('pricing.comparison.featureLabel')}
              </div>
              {visiblePlans.map((p) => (
                <div key={p.id} className="px-2 md:px-3 py-4 text-center">
                  <div className={`text-[10px] uppercase tracking-wider font-semibold ${p.accent.text} mb-1`}>{t(`pricing.plans.${p.id}.name`)}</div>
                  <div className="text-[10px] text-white/50 hidden md:block">
                    {`${t(`pricing.plans.${p.id}.price`)} ${t(`pricing.plans.${p.id}.price`) !== 'Custom' && t(`pricing.plans.${p.id}.price`) !== 'Gratis' ? t(`pricing.plans.${p.id}.priceSuffix`) : ''}`}
                  </div>
                </div>
              ))}
            </div>

            {(t('pricing.comparison.groups', { returnObjects: true }) || []).map((group, gi) => (
              <div key={group.title}>
                <div className={`grid grid-cols-[1.4fr_1fr_1fr_1fr] bg-[#111117]/70 border-t border-white/8 ${gi === 0 ? 'border-t-0' : ''}`}>
                  <div className="col-span-4 px-4 md:px-6 py-3 text-[11px] uppercase tracking-[0.15em] text-white/70 font-bold">
                    {group.title}
                  </div>
                </div>
                {group.rows.map((row, ri) => (
                  <div
                    key={row.label}
                    className={`grid grid-cols-[1.4fr_1fr_1fr_1fr] items-center border-t border-white/5 ${ri % 2 === 1 ? 'bg-white/[0.015]' : ''}`}
                  >
                    <div className="px-4 md:px-6 py-3.5 text-[13px] md:text-sm text-white/80">{row.label}</div>
                    {row.values.map((v, ci) => (
                      <div key={ci} className="px-1.5 md:px-3 py-3.5">
                        <Cell value={v} accent={visiblePlans[ci].accent} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* FAQ */}
      <section className="py-20 lg:py-24 bg-[#08080c] border-y border-white/5">
        <div className="container-mi max-w-3xl">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-4">{t('pricing.faqEyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
              {t('pricing.faqTitle')}
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {(t('pricing.faqs', { returnObjects: true }) || []).map((faq, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="rounded-2xl bg-[#111116] border border-white/8 px-6 data-[state=open]:border-violet-400/30"
              >
                <AccordionTrigger className="text-left text-base md:text-lg font-medium hover:no-underline py-5">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/70 leading-relaxed pb-5 text-[15px]">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-mi">
          <div className="relative overflow-hidden rounded-3xl border border-violet-400/20 bg-gradient-to-br from-[#141425] via-[#101018] to-[#0e0e14] p-10 md:p-14 text-center">
            <div className="absolute -top-32 -right-24 w-[500px] h-[500px] bg-violet-500/15 rounded-full blur-[100px]" />
            <div className="absolute -bottom-32 -left-24 w-[500px] h-[500px] bg-amber-500/12 rounded-full blur-[100px]" />
            <div className="relative">
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
                {t('pricing.ctaTitle')}
              </h2>
              <p className="mt-4 text-white/70 max-w-xl mx-auto">
                {t('pricing.ctaSubtitle')}
              </p>
              <div className="mt-8 flex justify-center gap-3 flex-wrap">
                <Link to={to('/request-demo')}>
                  <Button data-testid="pricing-cta-demo-btn" className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-7 rounded-full font-semibold">
                    {t('pricing.ctaSales')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to={to('/vende-tus-fotos')}>
                  <Button data-testid="pricing-cta-photographers-btn" variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    {t('pricing.ctaFree')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
